// Opening a game: link -> commit -> verified manifest -> verified files.
import { manifestPath, type GameLink } from '../../shared/links.ts';
import { sha256Hex } from '../../shared/hash.ts';
import { allFiles, LIMITS, validateManifest, type Manifest } from '../../shared/manifest.ts';
import type { Endpoints } from './config.ts';
import { RtwError } from './errors.ts';
import { download, getVerified, type ByteCache } from './fetcher.ts';
import { rawUrl, resolveCommit, type FetchFn } from './github.ts';
import { getEntry, putEntry, type KV } from './library.ts';
import { checkModule } from '../../shared/module-check.ts';
import { looksLikeRom } from '../../shared/leak.ts';

export interface GameDeps { endpoints: Endpoints; cache: ByteCache | null; fetchFn?: FetchFn; kv?: KV | null }

export interface OpenedGame {
  link: GameLink;
  commit: string;
  /** Set when the repo has moved on since the commit this device pinned. */
  updateCommit?: string;
  /** True when GitHub couldn't be reached and the pinned commit was used. */
  offline: boolean;
  manifest: Manifest;
  manifestBytes: Uint8Array;
  buildId: string;
  warnings: string[];
  totalSize: number;
}

export interface GameFiles { wasm: Uint8Array; js?: Uint8Array; data: Record<string, Uint8Array> }

function repoKey(l: GameLink): string {
  return `${l.owner.toLowerCase()}/${l.repo.toLowerCase()}`;
}

export async function openGame(link: GameLink, deps: GameDeps, opts: { forceCommit?: string } = {}): Promise<OpenedGame> {
  const kv = deps.kv;
  const pinned = opts.forceCommit ?? getEntry(link, kv)?.commit;
  let latest: string | undefined;
  let offline = false;
  try {
    latest = await resolveCommit(deps.endpoints, link.owner, link.repo, link.ref, deps.fetchFn);
  } catch (e) {
    if (!pinned || !(e instanceof RtwError) || (e.kind !== 'network' && e.kind !== 'rate-limit')) throw e;
    offline = true;
  }
  const commit = pinned ?? latest!;
  const mPath = manifestPath(link);
  const url = rawUrl(deps.endpoints, link.owner, link.repo, commit, mPath);
  const cacheKey = `${repoKey(link)}/manifest/${commit}/${mPath}`;
  let bytes = await deps.cache?.get(cacheKey) ?? null;
  if (!bytes) {
    try {
      bytes = await download(url, { fetchFn: deps.fetchFn, maxBytes: LIMITS.manifestBytes });
    } catch (e) {
      if (e instanceof RtwError && e.kind === 'not-found') {
        throw new RtwError('manifest', 'This repository has no retrowser.json, so it is not a retrowser game.', `${url}: HTTP 404`);
      }
      throw e;
    }
  }
  let json: unknown;
  try { json = JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(bytes)); } catch (e) {
    throw new RtwError('manifest', "The game's retrowser.json is not valid JSON.", `${url}: ${String(e)}`);
  }
  const v = validateManifest(json);
  if (!v.ok) throw new RtwError('manifest', "The game's retrowser.json does not follow the retrowser standard.", `${url}\n${v.errors.join('\n')}`);
  await deps.cache?.put(cacheKey, bytes);
  const m = v.manifest;
  const totalSize = allFiles(m).reduce((a, f) => a + f.size, 0);
  putEntry({ link, commit, id: m.id, title: m.title, platform: m.platform }, kv);
  return {
    link, commit, offline, manifest: m, manifestBytes: bytes, buildId: await sha256Hex(bytes), warnings: v.warnings, totalSize,
    ...(pinned && latest && latest !== pinned ? { updateCommit: latest } : {}),
  };
}

/** Fetch (or load from cache) and verify every file the manifest lists. */
export async function loadFiles(g: OpenedGame, deps: GameDeps, onProgress?: (loaded: number, total: number | null) => void, signal?: AbortSignal): Promise<GameFiles> {
  const files = allFiles(g.manifest);
  const dir = g.link.path ? g.link.path + '/' : '';
  const loaded = new Map<string, number>();
  const total = g.totalSize;
  let sum = 0;
  const report = () => { sum = [...loaded.values()].reduce((a, b) => a + b, 0); onProgress?.(sum, total); };
  const out = await Promise.all(files.map(async (f) => {
    const r = await getVerified(f, {
      url: rawUrl(deps.endpoints, g.link.owner, g.link.repo, g.commit, dir + f.path),
      repoKey: repoKey(g.link), commit: g.commit, cache: deps.cache, maxBytes: LIMITS.fileBytes, fetchFn: deps.fetchFn, signal,
      onProgress: (n) => { loaded.set(f.path, n); report(); },
    });
    if (sum > LIMITS.gameBytes) throw new RtwError('too-big', 'This game is larger than the site allows.', `${sum} > ${LIMITS.gameBytes} bytes`);
    return [f.path, r.bytes] as const;
  }));
  const map = new Map(out);
  // Strict gate before anything runs: no file may look like a ROM, and the wasm must be a retrowser module.
  for (const [path, bytes] of map) {
    const romLike = looksLikeRom(bytes);
    if (romLike) throw new RtwError('module', 'This build contains game data, so retrowser will not run it.', `${path}: ${romLike}`);
  }
  const check = checkModule(g.manifest, map.get(g.manifest.files.wasm.path)!);
  if (check.errors.length) {
    throw new RtwError('module', 'This build does not follow the retrowser standard, so retrowser will not run it.', check.errors.join('\n'));
  }
  const data: Record<string, Uint8Array> = {};
  for (const f of g.manifest.files.data ?? []) data[f.path] = map.get(f.path)!;
  return { wasm: map.get(g.manifest.files.wasm.path)!, ...(g.manifest.files.js ? { js: map.get(g.manifest.files.js.path)! } : {}), data };
}
